import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Member, OrgRole } from '@lizrd/contracts';
import { grantDomainAccess, listMembers, revokeDomainAccess } from '../lib/api';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { SkeletonRows } from './ui/skeleton';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const PAGE_SIZE = 25;

// Administrators and Managers reach every domain implicitly (RBAC-005/006);
// only Engineers and Viewers gain access through an explicit membership, so only
// they get an Add/Remove toggle.
const ORG_ADMIN_ROLES: OrgRole[] = ['ADMINISTRATOR', 'MANAGER'];

const ROLE_VARIANT: Record<OrgRole, 'info' | 'neutral'> = {
  ADMINISTRATOR: 'info',
  MANAGER: 'info',
  ENGINEER: 'neutral',
  VIEWER: 'neutral',
};

/**
 * "Manage Access" section on the domain Settings tab (RBAC-006, ENV-007,
 * paying down TD-008). Lists every organization user with their role and whether
 * they can access this domain, with client-side search and 25/page
 * pagination, and add/remove for the env-scoped roles.
 */
export function DomainAccessManager({ domainId }: { domainId: string }) {
  const qc = useQueryClient();
  const members = useQuery({ queryKey: ['members'], queryFn: listMembers });
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['members'] });
  const grant = useMutation({
    mutationFn: (userId: string) => grantDomainAccess(domainId, userId),
    onSuccess: invalidate,
  });
  const revoke = useMutation({
    mutationFn: (userId: string) => revokeDomainAccess(domainId, userId),
    onSuccess: invalidate,
  });
  const pendingId = grant.isPending
    ? grant.variables
    : revoke.isPending
      ? revoke.variables
      : undefined;

  const all = members.data ?? [];
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (m) =>
        (m.name ?? '').toLowerCase().includes(needle) || m.email.toLowerCase().includes(needle),
    );
  }, [all, q]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const clampedPage = Math.min(page, pageCount - 1);
  const start = clampedPage * PAGE_SIZE;
  const visible = filtered.slice(start, start + PAGE_SIZE);

  return (
    <section id="manage-access" className="scroll-mt-4 space-y-3">
      <div>
        <h2 className="font-display text-lg font-bold text-bone">Manage Access</h2>
        <p className="text-xs text-muted">
          Grant or remove a teammate&apos;s access to this domain. Administrators and managers reach
          every domain automatically.
        </p>
      </div>

      <Input
        placeholder="Search people by name or email…"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setPage(0);
        }}
      />

      {members.isLoading ? (
        <SkeletonRows rows={5} rightBlocks={1} />
      ) : (
        <Card className="divide-y divide-hairline">
          {filtered.length === 0 && (
            <p className="p-4 text-sm text-muted">
              {q ? 'No people match your search.' : 'No people in your organization yet.'}
            </p>
          )}
          {visible.map((m) => (
            <AccessRow
              key={m.id}
              member={m}
              hasAccess={m.domainIds.includes(domainId)}
              busy={pendingId === m.id}
              onGrant={() => grant.mutate(m.id)}
              onRevoke={() => revoke.mutate(m.id)}
            />
          ))}
        </Card>
      )}

      {filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-muted">
          <span>
            Showing {start + 1}–{Math.min(start + PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              disabled={clampedPage === 0}
              onClick={() => setPage(clampedPage - 1)}
            >
              Previous
            </Button>
            <span>
              Page {clampedPage + 1} of {pageCount}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={clampedPage >= pageCount - 1}
              onClick={() => setPage(clampedPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function AccessRow({
  member,
  hasAccess,
  busy,
  onGrant,
  onRevoke,
}: {
  member: Member;
  hasAccess: boolean;
  busy: boolean;
  onGrant: () => void;
  onRevoke: () => void;
}) {
  const implicit = ORG_ADMIN_ROLES.includes(member.role);
  return (
    <div className="flex items-center justify-between gap-3 p-4">
      <div className="min-w-0">
        <p className="truncate font-medium text-bone">{member.name ?? member.email}</p>
        <p className="truncate text-xs text-muted">{member.email}</p>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={ROLE_VARIANT[member.role]}>{member.role.toLowerCase()}</Badge>
        {implicit ? (
          <Badge variant="success">full access</Badge>
        ) : hasAccess ? (
          <>
            <Badge variant="success">has access</Badge>
            <Button size="sm" variant="ghost" disabled={busy} onClick={onRevoke}>
              Remove
            </Button>
          </>
        ) : (
          <Button size="sm" disabled={busy} onClick={onGrant}>
            Add
          </Button>
        )}
      </div>
    </div>
  );
}
